import React, { useState, useEffect } from "react";
import "./TalentCard.css";
import iconMoney from "../../assets/fi_10349139.png";
import iconCLose from "../../assets/Group (3).png";
import iconCheck from "../../assets/IconCheck.png";
import heart from "../../assets/heartIcon.svg";
import heartInactive from "../../assets/heartIconInactive.svg";
import iconSelfieLetter from "../../assets/icon-selfie-letter.svg";
import TalentAvatar from '../TalentAvatar/TalentAvatar';
import { TagLabel } from '../TagLabel/TagLabel';
import { FavoriteButton } from '../FavoriteButton';
import TalentTagsEditor from '../TalentTagsEditor/TalentTagsEditor';
import TalentTagCard from '../TalentTagCard/TalentTagCard';

export const TalentCard = ({
  talent,
  onUpdateFavorites,
  onTagsUpdate,
  tagsExpanded: tagsExpandedProp,
  onToggleTagsExpand: onToggleTagsExpandProp
}) => {
  // Local state as fallback if props are not provided
  const [localTagsExpanded, setLocalTagsExpanded] = useState(() => {
    const saved = localStorage.getItem('@ecomlovers:talentCardTagsExpanded');
    return saved ? JSON.parse(saved) : false;
  });

  const [isFlipped, setIsFlipped] = useState(false);

  const handleLocalToggleTagsExpand = () => {
    const newState = !localTagsExpanded;
    setLocalTagsExpanded(newState);
    localStorage.setItem('@ecomlovers:talentCardTagsExpanded', JSON.stringify(newState));
  };

  // Use the prop value if provided, otherwise use the local state
  const tagsExpanded = tagsExpandedProp !== undefined ? tagsExpandedProp : localTagsExpanded;
  // Local state for tags to support immediate UI update without full refresh
  const [currentTags, setCurrentTags] = useState(talent.tags || []);

  // Sync if prop changes (e.g. initial load or external refresh)
  useEffect(() => {
    setCurrentTags(talent.tags || []);
  }, [talent.tags]);

  // Listen for global tag updates (edits) to update UI immediately across all cards
  useEffect(() => {
    const handleTagGlobalUpdate = (event) => {
      const updatedTag = event.detail;
      if (!updatedTag || !updatedTag.id) return;

      setCurrentTags((prev) =>
        prev.map(t => t.id === updatedTag.id ? { ...t, ...updatedTag } : t)
      );
    };

    window.addEventListener('tag-updated', handleTagGlobalUpdate);
    return () => window.removeEventListener('tag-updated', handleTagGlobalUpdate);
  }, []);

  const handleLocalTagsUpdate = (newTags) => {
    if (Array.isArray(newTags)) {
      setCurrentTags(newTags);
    }
  };

  const handleToggleTagsExpand = onToggleTagsExpandProp || handleLocalToggleTagsExpand;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const SalaryInfo = ({ isNegotiable }) => {
    const icon = isNegotiable ? iconCheck : iconCLose;
    const text = isNegotiable ? "Valor negociável" : "Mínimo não negociável";
    const iconSize = isNegotiable
      ? { width: "12px", height: "8.25px" }
      : { width: "11.29px", height: "15.06px" };

    return (
      <>
        <div className="containerInfoBox">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "18px",
              height: "18px",
            }}
          >
            <img
              src={iconMoney}
              alt="  "
              style={{ width: "16.13px", height: "16.13px" }}
            />
          </div>
          <div className="text">{formatCurrency(talent.salary)}</div>
        </div>
        <div className="containerInfoBox">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "18px",
              height: "18px",
            }}
          >
            <img src={icon} alt="" style={iconSize} />
          </div>
          <div className="text">{text}</div>
        </div>
      </>
    );
  };

  const abbreviateName = (name) => {
    if (!name) return '';
    if (name.length <= 17) return name;

    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0];
    return parts[0] + ' ' + parts[parts.length - 1];
  };

  const abbreviateLocation = (city, state, maxCityLength = 12) => {
    if (!city && !state) return '';
    if (!state) return city || '';
    if (!city) return state || '';

    const shortCity = city.length > maxCityLength ? city.slice(0, maxCityLength) + '...' : city;
    return `${shortCity} - ${state}`;
  };

  return (
    <div className={`talent-card ${isFlipped ? 'flipped' : ''}`}>
      <div className="talent-card-inner">
        <div className="talent-card-front">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              position: "relative"
            }}
          >
            <div className="headerPainelCard">
              <div style={{ position: 'relative' }}>
                <TalentAvatar
                  talent={talent}
                  width="42px"
                  height="42px"
                  permitLightbox={false}
                />
                {talent?.isSelfieLetterFilled && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: 5,
                      right: 5,
                      transform: "translate(50%, 50%)",
                      width: "20px",
                      height: "20px",
                      borderRadius: "40px",
                      border: "0.8px solid #E3E3E3",
                      backgroundColor: "#FFFFFF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 2,
                    }}
                  >
                    <img
                      src={iconSelfieLetter}
                      alt="Selfie Letter"
                      style={{ width: "9px", height: "5.73px" }}
                    />
                  </div>
                )}
              </div>
              <div>
                <div className="talent-name">{abbreviateName(talent?.name)}</div>
                <div className="textSecondary">{abbreviateLocation(talent.city, talent.state)}</div>
              </div>
            </div>

            <FavoriteButton
              talent={talent}
              heartIcon={heart}
              heartInactiveIcon={heartInactive}
              iconSize={{ width: "14px", height: "11.99px" }}
              onUpdateFavorites={onUpdateFavorites}
            />
          </div>

          <div className="containerCulturalFit">
            {(() => {
              const fit = talent?.culturalFitResult || {};
              const total = Object.values(fit).reduce((sum, v) => sum + v, 0) || 1;

              if (
                Object.keys(fit).length === 0 ||
                Object.values(fit).every((v) => v === 0)
              ) {
                return (
                  <TagLabel text="Não respondido" />
                );
              }

              const values = Object.values(fit).map(v => (v / total) * 100);

              const hasThreeEqualAbove25 = Object.values(
                values.reduce((acc, val) => {
                  if (val > 25) {
                    const rounded = Math.round(val);
                    acc[rounded] = (acc[rounded] || 0) + 1;
                  }
                  return acc;
                }, {})
              ).some(count => count === 3);

              const noneAbove30 = values.every(v => v < 30);
              const allBelow30 = values.every(v => v < 30);

              const isBalanced = (hasThreeEqualAbove25 && noneAbove30) || allBelow30;

              const labels = {
                a: 'Analítico',
                i: 'Idealizador',
                c: 'Comunicador',
                e: 'Executor',
              };

              if (isBalanced) {
                return (
                  <TagLabel text="Equilibrado" />
                );
              }

              return Object.entries(fit)
                .map(([key, value]) => ({ key, value }))
                .sort((a, b) => b.value - a.value)
                .slice(0, 2)
                .map(({ key }, index) => (
                  <TagLabel key={index} text={labels[key]} />
                ));
            })()}
          </div>

          <div className="boxInfoTalent">
            <SalaryInfo
              isNegotiable={talent.salaryNegotiable}
              salaryMin={talent.salaryMin}
              salaryMax={talent.salaryMax}
            />
          </div>

          <div className="talentCardTags">
            <TalentTagsEditor
              talentId={talent.id}
              talentTags={currentTags}
              onTagsUpdate={handleLocalTagsUpdate}
              expanded={tagsExpanded}
              onToggleExpand={handleToggleTagsExpand}
              onEditRequest={() => setIsFlipped(true)}
            />
          </div>

          <div className="viewTalentButton">
            <button
              onClick={() =>
                window.open(`/companies/talents/profile/${talent?.id}`, '_blank')
              }
            >
              Perfil do talento
            </button>
          </div>
        </div>

        <div className="talent-card-back">
          <TalentTagCard
            talentId={talent.id}
            talentTags={currentTags}
            onTagsUpdate={handleLocalTagsUpdate}
            onFlipBack={() => setIsFlipped(false)}
          />
        </div>
      </div>
    </div>
  );
};
